import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useHomeLayout } from "@/hooks/useHomeLayout";
import { DialogShell } from "./DialogShell";
import "./HomeCustomize.css";

/**
 * ef-061-5 — the "Customize Home" picker. A button that opens a modal dialog with the tiles on Home (move up /
 * move down / remove), the tiles that can still be added, "Reset to default" and "Done" (ef-061 decision D2:
 * buttons, not drag).
 *
 * EVERY ACTION SAVES AT ONCE through `useHomeLayout()`, and both lists (and Home behind the dialog) render from
 * the hook's state — the server's answer — so they always agree. A failed save leaves the hook's state alone;
 * this shows an alert and the lists stay as they were.
 *
 * TWO QUICK CLICKS. The hook's state only changes once the server answers, so a second click made while the
 * first save is in flight would be computed from the stale list and undo or repeat it. Edits therefore build on
 * `pending` (the order last asked for) and go to the server one at a time through a promise chain: each click
 * applies exactly once and the server's final answer is the order the user built. A failure drops the clicks
 * queued behind it (they were built on an order the server never accepted).
 *
 * Shaped like EmployeeFormDialog: mounts per open, focus trap, Escape / overlay / Done close, focus returns
 * to the trigger.
 */
export function HomeCustomize() {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  return (
    <div className="home-customize">
      <button
        ref={triggerRef}
        type="button"
        className="btn btn--ghost home-customize__open"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        Customize Home
      </button>
      {open && <CustomizeDialog onClose={close} />}
    </div>
  );
}

function CustomizeDialog({ onClose }: { onClose: () => void }) {
  const { status, tiles, available, saving, save, reset } = useHomeLayout();
  const [failed, setFailed] = useState(false);
  const [resetting, setResetting] = useState(false);

  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // The order last asked for (null = nothing in flight; read the hook's state) and the serial queue.
  const tilesRef = useRef(tiles);
  tilesRef.current = tiles;
  const pending = useRef<string[] | null>(null);
  const chain = useRef<Promise<void>>(Promise.resolve());
  const outstanding = useRef(0);
  const generation = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  function enqueue(build: (current: string[]) => string[] | null): void {
    const current = pending.current ?? tilesRef.current.map((t) => t.id);
    const next = build(current);
    if (next === null) return;
    pending.current = next;
    setFailed(false);
    const gen = generation.current;
    outstanding.current += 1;
    chain.current = chain.current
      .then(async () => {
        if (gen !== generation.current) return; // an earlier save failed; this click was built on it
        await save(next);
      })
      .catch(() => {
        generation.current += 1;
        pending.current = null;
        if (mounted.current) setFailed(true);
      })
      .finally(() => {
        outstanding.current -= 1;
        if (outstanding.current === 0) pending.current = null;
      });
  }

  function move(id: string, by: -1 | 1): void {
    enqueue((ids) => {
      const from = ids.indexOf(id);
      const to = from + by;
      if (from < 0 || to < 0 || to >= ids.length) return null;
      const next = [...ids];
      [next[from], next[to]] = [next[to] as string, next[from] as string];
      return next;
    });
  }

  const remove = (id: string): void =>
    enqueue((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : null));

  const add = (id: string): void => enqueue((ids) => (ids.includes(id) ? null : [...ids, id]));

  function handleReset(): void {
    setFailed(false);
    setResetting(true);
    outstanding.current += 1;
    chain.current = chain.current
      .then(async () => {
        await reset();
      })
      .catch(() => {
        generation.current += 1;
        if (mounted.current) setFailed(true);
      })
      .finally(() => {
        outstanding.current -= 1;
        if (outstanding.current === 0) pending.current = null;
        if (mounted.current) setResetting(false);
      });
  }

  const lastIndex = tiles.length - 1;
  const dialogState = failed ? "error" : saving || resetting ? "saving" : status === "loading" ? "loading" : "ready";

  return (
    <DialogShell
      title="Customize Home"
      titleId={titleId}
      size="lg"
      onClose={onClose}
      busy={saving || resetting}
      dismissWhileBusy
      dataState={dialogState}
      footer={
        <>
          <button type="button" className="btn btn--ghost" disabled={resetting} onClick={handleReset}>
            Reset to default
          </button>
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Done
          </button>
        </>
      }
    >
      {failed && (
        <p className="home-customize-dialog__error" role="alert">
          Couldn't save your layout.
        </p>
      )}

      <section className="home-customize-dialog__section">
        <h3 id={`${titleId}-on`} className="home-customize-dialog__heading">
          Tiles on Home
        </h3>
        {tiles.length === 0 ? (
          <p className="home-customize-dialog__empty" data-state="empty">
            {status === "loading" ? "Loading your layout." : "No tiles on Home."}
          </p>
        ) : (
          <ul className="home-customize-dialog__list" aria-labelledby={`${titleId}-on`} data-state="ready">
            {tiles.map((tile, i) => (
              <li key={tile.id} className="home-customize-dialog__item">
                <span className="home-customize-dialog__name">{tile.title}</span>
                <span className="home-customize-dialog__buttons">
                  <button
                    type="button"
                    className="btn btn--ghost home-customize-dialog__btn"
                    aria-label={`Move ${tile.title} up`}
                    disabled={i === 0 || resetting}
                    onClick={() => move(tile.id, -1)}
                  >
                    <span aria-hidden="true">Up</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost home-customize-dialog__btn"
                    aria-label={`Move ${tile.title} down`}
                    disabled={i === lastIndex || resetting}
                    onClick={() => move(tile.id, 1)}
                  >
                    <span aria-hidden="true">Down</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost home-customize-dialog__btn"
                    aria-label={`Remove ${tile.title}`}
                    disabled={resetting}
                    onClick={() => remove(tile.id)}
                  >
                    <span aria-hidden="true">Remove</span>
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="home-customize-dialog__section">
        <h3 id={`${titleId}-add`} className="home-customize-dialog__heading">
          Tiles you can add
        </h3>
        {available.length === 0 ? (
          status === "ready" && (
            <p className="home-customize-dialog__empty" data-state="empty">
              Every tile is on Home.
            </p>
          )
        ) : (
          <ul className="home-customize-dialog__list" aria-labelledby={`${titleId}-add`} data-state="ready">
            {available.map((tile) => (
              <li key={tile.id} className="home-customize-dialog__item">
                <span className="home-customize-dialog__name">{tile.title}</span>
                <button
                  type="button"
                  className="btn btn--ghost home-customize-dialog__btn"
                  aria-label={`Add ${tile.title}`}
                  disabled={resetting}
                  onClick={() => add(tile.id)}
                >
                  <span aria-hidden="true">Add</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

    </DialogShell>
  );
}
